// Copyright (c) Ping. All rights reserved.

// Web-app developer console surface, mounted as `window.__ping.*`. Used
// from the browser DevTools to exercise endpoints + SDK flows end-to-end
// while the production UI for some features (linking, recovery, settings
// devices) is still in flight.
//
// All helpers assume the user has already signed in — the `api` client
// reads its bearer token from the auth interceptor, so anything that
// requires a JWT will 401 otherwise.
//
// Usage examples:
//
//   await window.__ping.listConversations()
//   await window.__ping.listSessions()
//   await window.__ping.revokeDevice('<base64url-device-id>')
//   await window.__ping.dumpSyncCursors()
//   await window.__ping.runLinkingDemo()
//   await window.__ping.uploadRecoveryBlob('passphrase here ≥8 chars')
//
// Intentionally NOT exported through any other file — this is a dev
// aid mounted once during session bootstrap (`sessionBootstrap.ts`)
// and not relied on by any production code path.

import { MessagingClient } from 'ping-openmls-sdk';

import {
  encryptRecoveryBlob,
  getRecoveryBlob,
  listConversations,
  listSessions,
  postRecoveryBlob,
  revokeDevice,
} from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';
import { bootstrapConversationsFromServer } from '@ping/ui/src/sync/bootstrapConversationsFromServer';
import { createWebConversationsPersister } from '@ping/ui/src/sync/conversationsPersister.web';

import { log } from '@core/log/log';
import { snapshotSyncCursors } from '@core/storage/idb';

import { getCurrentTransport } from './sessionBootstrap';

interface PingConsole {
  // --- conversations ---
  listConversations: () => Promise<unknown>;
  refreshConversationsCache: () => Promise<{ inserted: number; updated: number; serverCount: number }>;
  /** Dump every LOCAL conversation row split into what the list SHOWS vs what is
   *  FILTERED OUT (with the reason: soft-deleted / device-group / call-group).
   *  The single, auditable view of the list-membership filter. */
  listHiddenConversations: () => Promise<unknown>;
  // --- devices / sessions ---
  listSessions: () => Promise<unknown>;
  revokeDevice: (deviceId: string) => Promise<void>;
  // --- sync resume diagnostics ---
  dumpSyncCursors: () => Promise<ReadonlyArray<{ conversationIdHex: string; cursor: string }>>;
  // --- linking ---
  runLinkingDemo: () => Promise<{ ticket_id: string; expires_at: number }>;
  // --- recovery ---
  uploadRecoveryBlob: (passphrase: string) => Promise<{ ok: true; bytes: number }>;
  downloadRecoveryBlob: (passphrase: string, recoveryJwt: string) => Promise<{ ok: true; bytes: number }>;
  // --- misc ---
  whoami: () => { accountId: string | null; deviceId: Uint8Array | null };
}

declare global {
  interface Window {
    __ping?: Partial<PingConsole> & Record<string, unknown>;
  }
}

/**
 * Mounts (or augments) `window.__ping` with the dev-console surface.
 * Called from `bootstrapWebSession` after the session store transitions
 * to `authenticated`. Safe to call repeatedly — last write wins.
 */
export function mountConsoleApi(): void {
  if (typeof window === 'undefined') return;

  const api: PingConsole = {
    listConversations: async () => {
      const out = await listConversations();
      log.info({ event: 'console.listConversations', count: out.conversations?.length ?? 0 });
      return out;
    },

    refreshConversationsCache: async () => {
      const result = await bootstrapConversationsFromServer(createWebConversationsPersister());
      log.info({ event: 'console.refreshConversationsCache', ...result });
      return result;
    },

    listHiddenConversations: async () => {
      const { db } = await import('@core/persistence/db');
      const rows = await db.table<{
        id: string;
        name?: string;
        kind?: string;
        deletedAt?: number;
        archivedAt?: number;
      }>('conversations').toArray();
      const { partitionConversationVisibility } = await import(
        '@ping/ui/src/sync/conversationVisibility'
      );
      const { visible, hidden } = partitionConversationVisibility(rows);
      // Archived rows are visible in the list model but shown only under the
      // Archive tab — surface them separately so nothing looks "missing".
      const archived = visible.filter((r) => (r.archivedAt ?? 0) > 0);
      const summary = {
        total: rows.length,
        shownInList: visible.length - archived.length,
        archived: archived.map((r) => ({ id: r.id.slice(0, 12), name: r.name })),
        filteredOut: hidden.map((h) => ({
          id: h.row.id.slice(0, 12),
          name: h.row.name,
          reason: h.reason,
        })),
      };
      log.info({ event: 'console.listHiddenConversations', ...summary });
       
      console.table(summary.filteredOut);
      return summary;
    },

    listSessions: async () => {
      const out = await listSessions();
      log.info({ event: 'console.listSessions', count: out.devices.length });
      return out;
    },

    revokeDevice: async (deviceId) => {
      await revokeDevice(deviceId);
      log.info({ event: 'console.revokeDevice', deviceIdPrefix: deviceId.slice(0, 8) });
    },

    dumpSyncCursors: async () => {
      const rows = await snapshotSyncCursors();
      log.info({ event: 'console.dumpSyncCursors', count: rows.length });
      return rows;
    },

    // End-to-end smoke test that exercises the entire linking machinery
    // on a single device: generates an ephemeral identity for a fake
    // "new device", builds + seals a ticket to it, posts to BE, gets
    // the ticket back, opens it. If this resolves without throwing,
    // the BE linking-service + SDK seal/open primitives + transport
    // round-trip are all healthy.
    //
    // Does NOT call `consumeLinkingTicket` — that would mutate the
    // SDK state of the running client. To exercise consumption,
    // run a second tab as the "new device" and call
    // `window.__ping.acceptLinkingTicket(ticketId)`.
    runLinkingDemo: async () => {
      const status = useSessionStore.getState().status;
      if (status.kind !== 'authenticated') throw new Error('not authenticated');
      const client = status.client as unknown as {
        buildLinkingTicket?: (deviceId: Uint8Array, kp: Uint8Array) => Promise<unknown>;
      };
      if (typeof client.buildLinkingTicket !== 'function') {
        throw new Error('buildLinkingTicket not available on SDK client');
      }
      // The SDK exposes static `sealLinkingTicket` + `openLinkingTicket`
      // alongside `generateIdentity` etc.
      const MC = MessagingClient as unknown as {
        sealLinkingTicket?: (ticket: unknown, pub: Uint8Array) => Promise<Uint8Array>;
        openLinkingTicket?: (sealed: Uint8Array, priv: Uint8Array) => Promise<unknown>;
      };
      if (typeof MC.sealLinkingTicket !== 'function') {
        throw new Error('SDK.sealLinkingTicket unavailable — bump ping-openmls-sdk');
      }
      // Generate ephemeral key material for the imaginary new device.
      const newDeviceId = crypto.getRandomValues(new Uint8Array(32));
      const newDeviceKp = crypto.getRandomValues(new Uint8Array(128)); // SDK validates internally
      const ephKeyPair = await crypto.subtle.generateKey(
        { name: 'X25519' } as unknown as AlgorithmIdentifier,
        true,
        ['deriveBits'],
      );
      // Best-effort export — if the runtime doesn't have X25519 we throw.
      const pub = new Uint8Array(
        await crypto.subtle.exportKey('raw', (ephKeyPair as { publicKey: CryptoKey }).publicKey),
      );

      const ticket = await client.buildLinkingTicket(newDeviceId, newDeviceKp);
      const sealed = await MC.sealLinkingTicket!(ticket, pub);
      const sealedB64Url = bytesToUrlBase64(sealed);
      const { postLinkingTicket } = await import('@ping/ui/src/network/linking');
      const resp = await postLinkingTicket({ sealed: sealedB64Url });
      log.info({ event: 'console.runLinkingDemo', ticket_id: resp.ticket_id });
      return resp;
    },

    uploadRecoveryBlob: async (passphrase) => {
      const status = useSessionStore.getState().status;
      if (status.kind !== 'authenticated') throw new Error('not authenticated');
      // The cleartext we encrypt is the SDK identity export — what
      // `MessagingClient.recover` / `bootstrapSession` would need to
      // re-attach a fresh device. We reach for it via the same path
      // `loadOrCreateIdentity` uses.
      const { identityManager } = await import('@ping/ui/src/platform/identity.web');
      const identityExport = await identityManager.unwrapIdentity();
      if (identityExport === null) throw new Error('no identity to back up');
      const ciphertext = await encryptRecoveryBlob(identityExport, passphrase);
      await postRecoveryBlob({ ciphertext });
      log.info({ event: 'console.uploadRecoveryBlob', bytes: identityExport.length });
      return { ok: true as const, bytes: identityExport.length };
    },

    downloadRecoveryBlob: async (passphrase, recoveryJwt) => {
      const { decryptRecoveryBlob } = await import('@ping/ui/src/network/recovery');
      const blob = await getRecoveryBlob({ recoveryJwt });
      const cleartext = await decryptRecoveryBlob(blob.ciphertext, passphrase);
      log.info({ event: 'console.downloadRecoveryBlob', bytes: cleartext.length });
      return { ok: true as const, bytes: cleartext.length };
    },

    whoami: () => {
      // The session store's `identity` is the SDK's `IdentityHandle`
      // (an opaque byte-buffer wrapper), not the auth-layer identity
      // tuple. Account + device strings live in IndexedDB; expose
      // a lightweight projection so users can grep the deviceId for
      // revoke testing.
      const status = useSessionStore.getState().status;
      return {
        accountId: status.kind === 'authenticated' ? '<see Dexie identity row>' : null,
        deviceId: null,
      };
    },
  };

  const existing = (window.__ping ?? {}) as Record<string, unknown>;
  window.__ping = { ...existing, ...(api as unknown as Record<string, unknown>) };

  // Also surface the transport handle so console users can call its
  // public methods directly (revokeDeviceRemote, the welcome-recipient
  // primer, etc.) without round-tripping through this wrapper.
  window.__ping.transport = getCurrentTransport();

  log.info({ event: 'console.api.mounted' });
}

function bytesToUrlBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
